import { eventChannel } from 'redux-saga';
import { isMobile } from 'react-device-detect';

export enum VideoEvents {
  PLAYING = 'playing',
  PLAY = 'play',
  PAUSE = 'pause',
  CANPLAY = 'canplay',
  CANPLAYTHROUGH = 'canplaythrough',
  LOADEDDATA = 'loadeddata',
  PROGRESS = 'progress',
  ENDED = 'ended',
  TIMEUPDATE = 'timeupdate',
  ERROR = 'error',
  WAITING = 'waiting',
  SEEKED = 'seeked',
  SEEKING = 'seeking',
  EMPTIED = 'emptied', // Fired when the media has become empty
}

export function videoPlaybackEvents() {
  const videoEl: HTMLVideoElement = document.getElementById('videoEl') as HTMLVideoElement;
  return eventChannel(emitter => {
    // Native `timeupdate` only fires ~4x/sec, so end-detection can overshoot the
    // configured end by up to ~250ms. Poll on a tighter tick so the end check
    // lands within ~one frame and pauses forward on the end frame — no overshoot
    // and no corrective backward seek (a backward seek drops the element's
    // readyState, hiding the scene mesh mid-seek → the visible black "flash").
    // Note: we intentionally do NOT use requestVideoFrameCallback here — the
    // shared #videoEl is off-screen/throttled and three's VideoTexture relies on
    // rVFC for its own frame updates (see SceneMedia), so a second consumer must
    // not compete for it.
    const interval = setInterval(() => {
      if (videoEl && !videoEl.paused && videoEl.src) emitter({ type: 'timeupdate' });
    }, 30);
    videoEl.addEventListener(VideoEvents.PLAYING, emitter);
    videoEl.addEventListener(VideoEvents.PAUSE, emitter);
    videoEl.addEventListener(VideoEvents.PLAY, emitter);
    videoEl.addEventListener(VideoEvents.CANPLAY, emitter);
    videoEl.addEventListener(VideoEvents.CANPLAYTHROUGH, emitter);
    videoEl.addEventListener(VideoEvents.LOADEDDATA, emitter);
    videoEl.addEventListener(VideoEvents.PROGRESS, emitter);
    videoEl.addEventListener(VideoEvents.ENDED, emitter);
    videoEl.addEventListener(VideoEvents.TIMEUPDATE, emitter);
    videoEl.addEventListener(VideoEvents.ERROR, emitter);
    videoEl.addEventListener(VideoEvents.WAITING, emitter);
    videoEl.addEventListener(VideoEvents.SEEKED, emitter);
    videoEl.addEventListener(VideoEvents.SEEKING, emitter);
    videoEl.addEventListener(VideoEvents.EMPTIED, emitter);

    return () => {
      clearInterval(interval);
      videoEl.removeEventListener(VideoEvents.PLAYING, emitter);
      videoEl.removeEventListener(VideoEvents.PAUSE, emitter);
      videoEl.removeEventListener(VideoEvents.PLAY, emitter);
      videoEl.removeEventListener(VideoEvents.CANPLAY, emitter);
      videoEl.removeEventListener(VideoEvents.CANPLAYTHROUGH, emitter);
      videoEl.removeEventListener(VideoEvents.LOADEDDATA, emitter);
      videoEl.removeEventListener(VideoEvents.PROGRESS, emitter);
      videoEl.removeEventListener(VideoEvents.ENDED, emitter);
      videoEl.removeEventListener(VideoEvents.TIMEUPDATE, emitter);
      videoEl.removeEventListener(VideoEvents.ERROR, emitter);
      videoEl.removeEventListener(VideoEvents.WAITING, emitter);
      videoEl.removeEventListener(VideoEvents.SEEKED, emitter);
      videoEl.removeEventListener(VideoEvents.SEEKING, emitter);
      videoEl.removeEventListener(VideoEvents.EMPTIED, emitter);
    };
  });
}

export function dashPlayerEvents(DashPlayer, MediaPlayer) {
  return eventChannel(emitter => {
    for (const event in MediaPlayer.events) DashPlayer.on(MediaPlayer.events[event], emitter);
    return () => {
      for (const event in MediaPlayer.events) DashPlayer.off(MediaPlayer.events[event], emitter);
    };
  });
}

export function videoClickEvent(pause) {
  return eventChannel(emitter => {
    function playVideo() {
      videoPlayOnInteraction(pause, emitter);
    }
    if (isMobile) document.addEventListener('touchstart', playVideo, { once: true });
    else document.addEventListener('click', playVideo, { once: true });

    return () => {
      document.removeEventListener('touchstart', playVideo);
      document.removeEventListener('click', playVideo);
    };
  });
}

export async function videoPlayOnInteraction(paused, callback) {
  const videoEl: any = document.getElementById('videoEl');
  videoEl.setAttribute('muted', true);
  const hasSource = videoEl.getAttribute('src') != null;

  if (!hasSource) videoEl.setAttribute('src', '');

  // Playing the (muted) element within the user gesture is what unlocks it for
  // later unmuted playback on new sources. Call play() SYNCHRONOUSLY here (do not
  // defer it into a microtask/await — that loses the user-activation the unlock
  // depends on) and report the interaction back exactly once, promptly, whether
  // play() resolves or rejects. Firing the callback only after a deferred/retried
  // play() made the next button hang (up to the old 5s canplay timeout) and, with
  // stacked clicks, fire multiple times → skipped slides.
  let settled = false;
  const finish = async () => {
    if (settled) return;
    settled = true;
    videoEl.removeAttribute('muted');
    if (paused) {
      try {
        await videoEl.pause();
      } catch (e) {}
    }
    if (callback) callback(true);
  };

  try {
    const playPromise = videoEl.play();
    if (playPromise !== undefined) playPromise.then(finish).catch(finish);
    else finish();
  } catch (e) {
    finish();
  }
}
